import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import type { Division } from '@/lib/types/deck-builder';
import {
  getDivisionImagePath,
  getCountryFlagPath,
  getCoalitionFlagPath,
} from '@/lib/utils/division-images';
import {
  filterDivisionGroups,
  type DivisionSelectGroup,
} from '@/lib/utils/division-select-groups';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

function hideBrokenImg(e: { currentTarget: HTMLImageElement }) {
  e.currentTarget.style.display = 'none';
}

/** Plain left-click without modifiers — let Link/browser handle new-tab gestures. */
function isPlainPrimaryClick(e: React.MouseEvent) {
  return (
    e.button === 0 &&
    !e.metaKey &&
    !e.ctrlKey &&
    !e.shiftKey &&
    !e.altKey
  );
}

export interface DivisionGroupedPickerProps {
  divisionGroups: DivisionSelectGroup[];
  value: string;
  onValueChange: (descriptor: string) => void;
  selectedDivision: Division | undefined;
  triggerClassName?: string;
  /** When set, rows render as links so Ctrl/Cmd/middle-click opens the division URL in a new tab. */
  divisionHref?: (descriptor: string) => string;
}

export function DivisionGroupedPicker({
  divisionGroups,
  value,
  onValueChange,
  selectedDivision,
  triggerClassName,
  divisionHref,
}: DivisionGroupedPickerProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [activeOptionIndex, setActiveOptionIndex] = useState<number | null>(null);

  const listboxId = useId();
  const comboboxInputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const activeOptionIndexRef = useRef(activeOptionIndex);
  const flatOptionsRef = useRef<Division[]>([]);

  const filteredGroups = useMemo(
    () =>
      filterDivisionGroups(divisionGroups, filter, {
        alwaysIncludeDescriptor: value || undefined,
      }),
    [divisionGroups, filter, value]
  );

  const flatOptions = useMemo(
    () => filteredGroups.flatMap((g) => g.divisions),
    [filteredGroups]
  );

  const optionIndexByDescriptor = useMemo(() => {
    const m = new Map<string, number>();
    flatOptions.forEach((d, i) => m.set(d.descriptor, i));
    return m;
  }, [flatOptions]);

  activeOptionIndexRef.current = activeOptionIndex;
  flatOptionsRef.current = flatOptions;

  useEffect(() => {
    if (flatOptions.length === 0) {
      setActiveOptionIndex(null);
      return;
    }
    setActiveOptionIndex((prev) => {
      if (prev === null) return 0;
      if (prev >= flatOptions.length) return flatOptions.length - 1;
      return prev;
    });
  }, [flatOptions]);

  useEffect(() => {
    if (activeOptionIndex === null || activeOptionIndex < 0) return;
    const div = flatOptions[activeOptionIndex];
    if (!div) return;
    const el = document.getElementById(`division-opt-${div.descriptor}`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeOptionIndex, flatOptions]);

  const selectDivision = useCallback(
    (descriptor: string) => {
      onValueChange(descriptor);
      setOpen(false);
      setFilter('');
    },
    [onValueChange]
  );

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const flat = flatOptionsRef.current;
    const current = activeOptionIndexRef.current;

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        if (flat.length === 0) return;
        setActiveOptionIndex((i) => {
          if (i === null) return 0;
          return Math.min(i + 1, flat.length - 1);
        });
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        if (flat.length === 0) return;
        setActiveOptionIndex((i) => {
          if (i === null) return flat.length - 1;
          if (i <= 0) return null;
          return i - 1;
        });
        break;
      }
      case 'Home': {
        e.preventDefault();
        if (flat.length === 0) return;
        setActiveOptionIndex(0);
        break;
      }
      case 'End': {
        e.preventDefault();
        if (flat.length === 0) return;
        setActiveOptionIndex(flat.length - 1);
        break;
      }
      case 'Enter': {
        e.preventDefault();
        const idx = current ?? 0;
        const div = flat[idx];
        if (div) selectDivision(div.descriptor);
        break;
      }
      case 'Escape': {
        e.preventDefault();
        setOpen(false);
        break;
      }
      default:
        break;
    }
  };

  const activeDescendantId =
    activeOptionIndex !== null && flatOptions[activeOptionIndex]
      ? `division-opt-${flatOptions[activeOptionIndex]!.descriptor}`
      : undefined;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          const flat = filterDivisionGroups(divisionGroups, filter, {
            alwaysIncludeDescriptor: value || undefined,
          }).flatMap((g) => g.divisions);
          setActiveOptionIndex(flat.length ? 0 : null);
          queueMicrotask(() => inputRef.current?.focus());
        } else {
          setFilter('');
          setActiveOptionIndex(null);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={open ? listboxId : undefined}
          className={cn(
            'h-9 w-full max-w-sm justify-between border-input bg-transparent px-3 font-normal shadow-xs hover:bg-transparent dark:bg-input/30 dark:hover:bg-input/50',
            '[&_svg]:pointer-events-none [&_svg]:shrink-0',
            triggerClassName
          )}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2">
            {selectedDivision ? (
              <>
                <img
                  src={getDivisionImagePath(selectedDivision.descriptor)}
                  alt=""
                  className="h-5 w-5 shrink-0 object-contain"
                  onError={hideBrokenImg}
                />
                <span className="truncate">{selectedDivision.name}</span>
              </>
            ) : (
              <span className="truncate text-muted-foreground">Select a division</span>
            )}
          </span>
          <ChevronDown className="size-4 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="max-h-[min(90vh,32rem)] max-w-sm min-w-[min(100vw-2rem,24rem)] p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex max-h-[min(90vh,32rem)] flex-col">
          <div className="shrink-0 border-b border-border p-2">
            <Input
              ref={inputRef}
              id={comboboxInputId}
              type="search"
              autoComplete="off"
              placeholder="Filter divisions…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="h-8"
              role="combobox"
              aria-autocomplete="list"
              aria-controls={listboxId}
              aria-expanded={open}
              aria-activedescendant={activeDescendantId}
              aria-label="Filter divisions by name, country, or coalition"
              onKeyDown={handleInputKeyDown}
            />
          </div>
          <div
            id={listboxId}
            role="listbox"
            aria-label="Divisions"
            className="min-h-0 flex-1 overflow-y-auto p-1"
            tabIndex={-1}
          >
            {filteredGroups.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground" role="status">
                No divisions match your filter.
              </div>
            ) : (
              filteredGroups.map((group, groupIndex) => (
                <div key={`${group.allianceKey}-${group.country}`} role="group" aria-label={group.label}>
                  <div
                    className={cn(
                      'flex items-center gap-2 px-2 pb-1 pt-2 text-xs font-normal text-muted-foreground',
                      groupIndex > 0 && 'mt-2 border-t border-border/60 pt-3'
                    )}
                    aria-hidden
                  >
                    <img
                      src={getCoalitionFlagPath(group.allianceKey)}
                      alt=""
                      className="h-4 w-4 shrink-0 object-contain"
                      onError={hideBrokenImg}
                    />
                    <img
                      src={getCountryFlagPath(group.country)}
                      alt=""
                      className="h-4 w-4 shrink-0 object-contain"
                      onError={hideBrokenImg}
                    />
                    <span className="font-medium">{group.label}</span>
                  </div>
                  {group.divisions.map((div) => {
                    const idx = optionIndexByDescriptor.get(div.descriptor);
                    if (idx === undefined) return null;
                    const isActive = activeOptionIndex === idx;
                    const isSelected = value === div.descriptor;
                    const rowClass = cn(
                      'relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none',
                      divisionHref && 'text-foreground no-underline hover:no-underline',
                      isActive && 'bg-accent text-accent-foreground',
                      isSelected && !isActive && 'bg-muted/50'
                    );
                    const rowBody = (
                      <>
                        <img
                          src={getDivisionImagePath(div.descriptor)}
                          alt=""
                          className="h-5 w-5 shrink-0 object-contain"
                          onError={hideBrokenImg}
                        />
                        <span className="min-w-0 truncate">{div.name}</span>
                      </>
                    );
                    const rowHandlers = {
                      onMouseEnter: () => setActiveOptionIndex(idx),
                      onMouseDown: (e: React.MouseEvent) => {
                        if (e.button === 0) e.preventDefault();
                      },
                    };
                    if (divisionHref) {
                      return (
                        <Link
                          key={div.descriptor}
                          id={`division-opt-${div.descriptor}`}
                          role="option"
                          aria-selected={isSelected}
                          to={divisionHref(div.descriptor)}
                          className={rowClass}
                          {...rowHandlers}
                          onClick={(e) => {
                            if (isPlainPrimaryClick(e)) {
                              e.preventDefault();
                              selectDivision(div.descriptor);
                            }
                          }}
                        >
                          {rowBody}
                        </Link>
                      );
                    }
                    return (
                      <div
                        key={div.descriptor}
                        id={`division-opt-${div.descriptor}`}
                        role="option"
                        aria-selected={isSelected}
                        className={rowClass}
                        {...rowHandlers}
                        onClick={() => selectDivision(div.descriptor)}
                      >
                        {rowBody}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
